/**
 * VersionChip — the always-visible deployed-version indicator container. Wires
 * the compile-time frontend `BUILD_INFO` and the live `GET /api/version` backend
 * metadata into the presentational `VersionChipView`, which renders the chip and
 * its drift-aware detail popover.
 *
 * Mounted in the global AppFooter (normal users, admins, and the public
 * landing footer). Backend fetch failures degrade gracefully to the neutral
 * "unknown" state — the chip always renders the frontend build info.
 */

import React from 'react'

import { BUILD_INFO } from '@/shared/utils/constants'

import { useBackendVersion } from './useBackendVersion'
import { VersionChipView } from './VersionChipView'

interface VersionChipProps {
  /**
   * Direction the detail popover opens. Defaults to `'top'` since the chip
   * lives in a bottom footer, where a below-anchored popover would be clipped.
   */
  placement?: 'top' | 'bottom'
}

export const VersionChip: React.FC<VersionChipProps> = React.memo(function VersionChip({
  placement = 'top',
}) {
  const { data: backend } = useBackendVersion()

  return <VersionChipView frontend={BUILD_INFO} backend={backend} placement={placement} />
})

VersionChip.displayName = 'VersionChip'
